import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Contact } from 'src/app/core/models/contact.model';
import { ContactCacheService, normTexte } from 'src/app/core/services/entities/contact-cache.service';
import { ToastService } from './toast.service';

/** Valeur échangée : contact CRM (id + nom) ou nom libre */
export interface ContactChoisi { contactId?: number; contactNom?: string; contact?: Contact; }

let compteur = 0;

/**
 * Sélecteur de contact accessible (combobox) : recherche sans accents sur nom et téléphone,
 * contacts récents en tête, création en un geste (« + Créer “Nom” ») sans quitter le formulaire.
 */
@Component({
  selector: 'bay-contact-picker',
  template: `
    <div class="bay-picker">
      <label *ngIf="label" class="bay-label" [for]="inputId">{{ label }}</label>
      <div class="bay-picker-field">
        <span class="material-symbols-outlined bay-picker-icon" aria-hidden="true">{{ choisi?.contactId ? 'verified_user' : 'person_search' }}</span>
        <input #input [id]="inputId" type="text" class="bay-input bay-picker-input" autocomplete="off"
          role="combobox" aria-autocomplete="list" [attr.aria-expanded]="ouvert" [attr.aria-controls]="listeId"
          [attr.aria-activedescendant]="actif >= 0 ? listeId + '-' + actif : null"
          [placeholder]="placeholder" [value]="texte" [disabled]="disabled"
          (input)="onSaisie($any($event.target).value)" (focus)="ouvrir()" (blur)="fermerPlusTard()"
          (keydown)="onClavier($event)">
        <button *ngIf="texte" type="button" class="bay-icon-btn bay-picker-clear" (mousedown)="$event.preventDefault()"
          (click)="effacer()" aria-label="Effacer le contact">
          <span class="material-symbols-outlined" aria-hidden="true">close</span>
        </button>
      </div>
      <ul *ngIf="ouvert && (suggestions.length || peutCreer)" [id]="listeId" class="bay-picker-list" role="listbox">
        <li *ngFor="let c of suggestions; let i = index" [id]="listeId + '-' + i" role="option"
          class="bay-picker-option" [class.active]="i === actif" [attr.aria-selected]="i === actif"
          (mousedown)="$event.preventDefault(); choisir(c)">
          <span class="bay-avatar" aria-hidden="true">{{ c.nom?.charAt(0)?.toUpperCase() }}</span>
          <span class="min-w-0">
            <span class="bay-picker-nom">{{ c.nom }}</span>
            <span class="bay-picker-meta">{{ c.telephone || libelleType(c.type) }}</span>
          </span>
        </li>
        <li *ngIf="peutCreer" [id]="listeId + '-' + suggestions.length" role="option"
          class="bay-picker-option bay-picker-create" [class.active]="actif === suggestions.length"
          (mousedown)="$event.preventDefault(); creer()">
          <span class="material-symbols-outlined" aria-hidden="true">person_add</span>
          <span>{{ creation ? 'Création…' : 'Créer « ' + texte.trim() + ' »' }}<span class="bay-picker-meta"> · nouveau {{ type === 'FOURNISSEUR' ? 'fournisseur' : 'client' }}</span></span>
        </li>
      </ul>
      <p *ngIf="choisi?.contactNom && !choisi?.contactId && !ouvert" class="bay-hint">
        Nom libre, sans fiche contact.
        <button type="button" class="bay-link" (click)="creer()" [disabled]="creation">Créer la fiche</button>
      </p>
    </div>
  `
})
export class ContactPickerComponent implements OnInit, OnChanges, OnDestroy {
  @Input() type?: 'CLIENT' | 'FOURNISSEUR';
  @Input() label = '';
  @Input() placeholder = 'Rechercher ou saisir un nom';
  @Input() inputId = `bay-contact-${++compteur}`;
  @Input() disabled = false;
  /** Valeur initiale */
  @Input() contactId?: number | null;
  @Input() contactNom?: string | null;
  /** Pas « change » : le change natif de l'<input> interne remonterait jusqu'au parent (Event reçu → contact effacé) */
  @Output() contactChange = new EventEmitter<ContactChoisi>();
  @ViewChild('input') input?: ElementRef<HTMLInputElement>;

  readonly listeId = `${this.inputId}-liste`;
  contacts: Contact[] = [];
  suggestions: Contact[] = [];
  texte = '';
  choisi: ContactChoisi | null = null;
  ouvert = false;
  actif = -1;
  creation = false;
  private destroy$ = new Subject<void>();

  constructor(private cache: ContactCacheService, private toast: ToastService) {}

  ngOnInit(): void {
    this.cache.tous().pipe(takeUntil(this.destroy$)).subscribe(c => {
      this.contacts = c;
      this.appliquerValeur();
      this.filtrer();
    });
  }

  ngOnChanges(c: SimpleChanges): void {
    if (c.contactId || c.contactNom) this.appliquerValeur();
    if (c.type) this.filtrer();
  }

  ngOnDestroy(): void { this.destroy$.next(); this.destroy$.complete(); }

  private appliquerValeur(): void {
    const c = this.contactId ? this.contacts.find(x => x.id === this.contactId) : undefined;
    if (c) {
      this.choisi = { contactId: c.id, contactNom: c.nom, contact: c };
      this.texte = c.nom;
    } else if (this.contactNom) {
      this.choisi = { contactId: this.contactId || undefined, contactNom: this.contactNom };
      this.texte = this.contactNom;
    } else if (!this.contactId) {
      this.choisi = null;
      this.texte = '';
    }
  }

  get peutCreer(): boolean {
    const n = normTexte(this.texte);
    return !!n && n.length >= 2 && !this.contacts.some(c => normTexte(c.nom) === n);
  }

  libelleType(t: string): string {
    return t === 'FOURNISSEUR' ? 'Fournisseur' : t === 'LES_DEUX' ? 'Client et fournisseur' : 'Client';
  }

  onSaisie(v: string): void {
    this.texte = v;
    // Nom tapé identique à une fiche : rattaché sans clic
    const exact = this.cache.duType(this.contacts, this.type).find(c => normTexte(c.nom) === normTexte(v));
    this.choisi = v.trim() ? (exact ? { contactId: exact.id, contactNom: exact.nom, contact: exact } : { contactNom: v.trim() }) : null;
    this.contactChange.emit(this.choisi || {});
    this.filtrer();
    this.ouvert = true;
    this.actif = -1;
  }

  private filtrer(): void {
    const q = normTexte(this.texte);
    const base = this.cache.duType(this.contacts, this.type);
    const recents = this.cache.recents();
    const rang = (c: Contact) => { const i = recents.indexOf(c.id!); return i < 0 ? 999 : i; };
    this.suggestions = base
      .filter(c => !q || normTexte(c.nom).includes(q) || (c.telephone || '').replace(/\D/g, '').includes(q.replace(/\D/g, '') || '§'))
      .sort((a, b) => rang(a) - rang(b) || (a.nom || '').localeCompare(b.nom || '', 'fr'))
      .slice(0, 8);
  }

  ouvrir(): void { this.filtrer(); this.ouvert = true; }
  fermerPlusTard(): void { setTimeout(() => { this.ouvert = false; this.actif = -1; }, 150); }

  onClavier(e: KeyboardEvent): void {
    const n = this.suggestions.length + (this.peutCreer ? 1 : 0);
    if (e.key === 'ArrowDown') { e.preventDefault(); this.ouvert = true; this.actif = n ? (this.actif + 1) % n : -1; }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.actif = n ? (this.actif - 1 + n) % n : -1; }
    else if (e.key === 'Enter' && this.ouvert && this.actif >= 0) {
      e.preventDefault();
      if (this.actif < this.suggestions.length) this.choisir(this.suggestions[this.actif]); else this.creer();
    } else if (e.key === 'Escape' && this.ouvert) { e.stopPropagation(); this.ouvert = false; }
  }

  choisir(c: Contact): void {
    this.choisi = { contactId: c.id, contactNom: c.nom, contact: c };
    this.texte = c.nom;
    this.ouvert = false;
    this.actif = -1;
    this.cache.marquerUtilise(c.id);
    this.contactChange.emit(this.choisi);
  }

  creer(): void {
    const nom = this.texte.trim();
    if (!nom || this.creation) return;
    this.creation = true;
    this.cache.creer(nom, this.type === 'FOURNISSEUR' ? 'FOURNISSEUR' : 'CLIENT').subscribe({
      next: c => {
        this.creation = false;
        this.contacts = this.cache.snapshot;
        this.choisir(c);
        this.toast.succes(`Fiche « ${c.nom} » créée`);
      },
      error: err => { this.creation = false; this.toast.erreurHttp(err, 'Impossible de créer ce contact.'); }
    });
  }

  effacer(): void {
    this.texte = '';
    this.choisi = null;
    this.contactChange.emit({});
    this.filtrer();
    setTimeout(() => this.input?.nativeElement.focus());
  }
}
